import { IconButton, Stack, useTheme } from "@mui/material";
import { ArrowDownRight, ArrowUpRight, Pencil } from "lucide-react";
import { BalanceBarChart } from "web_myplann/Finances/Components/BalanceBarChart";
import { BalanceBreakdownSection } from "web_myplann/Finances/Components/BalanceBreakdownSection";
import { BalanceHero } from "web_myplann/Finances/Components/BalanceHero";
import { BalanceLeadAction } from "web_myplann/Finances/Components/BalanceLeadAction";
import { BalanceRecommendation } from "web_myplann/Finances/Components/BalanceRecommendation";
import { FinancesEmptyState } from "web_myplann/Finances/Components/FinancesEmptyState";
import { FinancesErrorState } from "web_myplann/Finances/Components/FinancesErrorState";
import { isPositiveFinancialBalance } from "web_myplann/Finances/Helpers/isPositiveFinancialBalance";
import { useFinanceData } from "web_myplann/Finances/Hooks/useFinanceData";
import { useFinancesEdit } from "web_myplann/Finances/Hooks/useFinancesEdit";
import { useFinancesWizardNavigation } from "web_myplann/Finances/Hooks/useFinancesWizardNavigation";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";

const R = Resources.Finances;

export const FinancesIntroV2 = () => {
	const { t } = useResource();
	const theme = useTheme();
	const {
		isLoading,
		isError,
		totalIncome,
		totalExpenses,
		incomeBreakdown,
		expenseBreakdown,
	} = useFinanceData();
	const { isFromProfile } = useFinancesWizardNavigation();
	const { onEdit } = useFinancesEdit();

	const balance = totalIncome - totalExpenses;
	const positive = isPositiveFinancialBalance(balance);
	const isEmpty = totalIncome === 0 && totalExpenses === 0;
	const isReady = !isLoading && !isError;
	const showEmptyState = isReady && isEmpty;
	const showOverview = isReady && !isEmpty;

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={theme.palette.surface.muted}
			topBar={
				<MyPlannNavigationHeader
					title={t(R.title)}
					defaultRoute={
						isFromProfile
							? Routing.Profile.ProfileDetail.path()
							: Routing.Dashboard.path()
					}
					bgColor={theme.palette.background.appBar}
					endComponent={
						showOverview && (
							<IconButton
								variant="header"
								aria-label={t(R.Detail.editButton)}
								onClick={onEdit}
							>
								<Pencil
									size={theme.iconSize.md}
									color={theme.palette.grey[600]}
								/>
							</IconButton>
						)
					}
				/>
			}
		>
			{isLoading && <MyPlannLoading />}
			{isError && <FinancesErrorState />}
			{showEmptyState && <FinancesEmptyState onStart={onEdit} />}
			{showOverview && (
				<Stack pb={theme.spacingTokens.xxl}>
					<BalanceHero title={t(R.Detail.result)} balance={balance} size="md" />
					<BalanceBarChart income={totalIncome} expenses={totalExpenses} />

					<BalanceBreakdownSection
						sectionTitle={t(R.Detail.incomeSection)}
						icon={ArrowUpRight}
						iconColor={theme.palette.green[500]}
						totalLabel={t(R.Detail.totalIncome)}
						totalAmount={totalIncome}
						totalColor={theme.palette.green[600]}
						entries={incomeBreakdown}
					/>

					<BalanceBreakdownSection
						sectionTitle={t(R.Detail.expensesSection)}
						icon={ArrowDownRight}
						iconColor={theme.palette.red[500]}
						totalLabel={t(R.Detail.totalExpenses)}
						totalAmount={totalExpenses}
						totalColor={theme.palette.red[600]}
						entries={expenseBreakdown}
					/>

					<BalanceRecommendation
						positive={positive}
						description={
							positive
								? t(R.Recommendation.surplusDescription)
								: t(R.Recommendation.deficitDescription)
						}
					>
						<BalanceLeadAction
							positive={positive}
							income={totalIncome}
							expense={totalExpenses}
							size="md"
						/>
					</BalanceRecommendation>
				</Stack>
			)}
		</MyPlannLayout>
	);
};
